import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MESSAGES from "../../../../common/data/constants/messages";
import { IEmployee } from "../../../../common/data/dtos/employee";
import { IWorkResourceEmployee } from "../../../../common/data/dtos/work-resource-employee";
import ReturnStatusEnum from "../../../../common/data/enums/enumReturnStatus";
import WorkResourceEnum from "../../../../common/data/enums/enumWorkResouce";
import WorkResourceEmployeeStatusEnum, { getReturnReason, getStatusBadge } from "../../../../common/data/enums/enumWorkResourceEmployeeStatus";
import Badge from "../../../../components/bootstrap/Badge";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardActions, CardBody, CardFooter, CardFooterLeft, CardHeader, CardLabel, CardSubTitle, CardTitle } from "../../../../components/bootstrap/Card";
import Tooltips from "../../../../components/bootstrap/Tooltips";
import { MoreActions } from "../../../../components/extras/MoreActions";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import ThemeContext from "../../../../contexts/themeContext";
import { appMenu } from "../../../../menu";
import employeeService from "../../../../services/employee-service";
import workResourceEmployeeService from "../../../../services/work-resource-employee-service";
import LoadingPage from "../../loading/LoadingPage";
import { ReturnWorkResourceModal } from "./ReturnWorkResourceModal";
import { WorkResourceChangeSignatureModal } from "./WorkResourceChangeSignatureModal";
import { WorkResourceEmployeeModal } from "./WorkResourceEmployeeModal";
import { WorkResourceSignatureModal } from "./WorkResourceSignatureModal";
import { WorkResourceSignedModal } from "./WorkResourceSignedModal";

type TTabs = 'Fornecidos' | 'Não fornecidos';
interface ITabs {
    [key: string]: TTabs;
}

export const CommonWorkResourceEmployeeList = () => {

    const TABS: ITabs = {
        DELIVERED: 'Fornecidos',
        NOT_DELIVERED: 'Não fornecidos'
    };

    const { mobileDesign } = useContext(ThemeContext);
    const { id } = useParams();

    const [activeTab, setActiveTab] = useState<TTabs>(TABS.DELIVERED);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isModalReturnOpened, setIsModalReturnOpened] = useState(false);
    const [isModalSignatureOpened, setIsModalSignatureOpened] = useState(false);
    const [isModalSignedOpened, setIsModalSignedOpened] = useState(false);
    const [isModalChangeSignatureOpened, setIsModalChangeSignatureOpened] = useState(false);

    const [selectedEmployee, setSelectedEmployee] = useState<IEmployee | null>(null);
    const [workResourcesEmployee, setWorkResourcesEmployee] = useState<IWorkResourceEmployee[]>([]);
    const [selectedWorkResourceEmployee, setSelectedWorkResourceEmployee] = useState<IWorkResourceEmployee | null>(null);

    const [notDeliveredWorkResourcesEmployee, setNotDeliveredWorkResourcesEmployee] = useState<IWorkResourceEmployee[]>([]);
    const [signItemType, setSignItemType] = useState<WorkResourceEnum>();

    useEffect(() => {
        if (id) {
            fetchData();
            setSelectedWorkResourceEmployee(null);
        }
    }, []);

    const fetchData = async () => {

        setIsLoading(true);

        try {
            if (!id) {
                showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION)
            } else {

                const [employeeResponse, workResourceEmployeeResponse] = await Promise.all([
                    employeeService.getOneEmployee(id),
                    workResourceEmployeeService.getWorkResourceEmployeeByEmployee(id)
                ]);

                if (employeeResponse.error) {
                    showWarning(employeeResponse.error);
                    return;
                }

                if (workResourceEmployeeResponse.error) {
                    showWarning(workResourceEmployeeResponse.error);
                    return;
                }

                setSelectedEmployee(employeeResponse.employee || null);

                if (workResourceEmployeeResponse.workResourceEmployees) {

                    const notDelivered = workResourceEmployeeResponse.workResourceEmployees.filter(item => item.situation === WorkResourceEmployeeStatusEnum.notDelivered);
                    const delivered = workResourceEmployeeResponse.workResourceEmployees.filter(item => item.situation !== WorkResourceEmployeeStatusEnum.notDelivered);

                    setNotDeliveredWorkResourcesEmployee(notDelivered || [])
                    setWorkResourcesEmployee(delivered || []);
                } else {
                    setNotDeliveredWorkResourcesEmployee([])
                    setWorkResourcesEmployee([]);
                }
            }
        } catch (error) {
            console.error('Erro nas requisições:', error);
            showWarning('Erro ao carregar dados');
        } finally {
            setIsLoading(false);
        }
    }

    const fetchWorkResourcesEmployee = async () => {
        setIsLoading(true);

        if (!id) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            setIsLoading(false);
        } else {

            const response = await workResourceEmployeeService.getWorkResourceEmployeeByEmployee(id);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            if (response.workResourceEmployees) {

                const notDelivered = response.workResourceEmployees.filter(item => item.situation === WorkResourceEmployeeStatusEnum.notDelivered);
                const delivered = response.workResourceEmployees.filter(item => item.situation !== WorkResourceEmployeeStatusEnum.notDelivered);

                setNotDeliveredWorkResourcesEmployee(notDelivered || [])
                setWorkResourcesEmployee(delivered || []);
            } else {
                setNotDeliveredWorkResourcesEmployee([])
                setWorkResourcesEmployee([]);
            }
        }
    };

    const toggleModal = (setter: React.Dispatch<React.SetStateAction<boolean>>, isVisible: boolean) => {
        setter(isVisible);
    };

    const handleAddClick = () => {
        toggleModal(setIsModalOpened, true);
    };

    const handleReturnClick = (workResourceEmployee: IWorkResourceEmployee) => {
        if (workResourceEmployee.returnStatus !== ReturnStatusEnum.none) {
            showWarning(MESSAGES.VALIDATION.INVALID_STATUS);
        } else {
            setSelectedWorkResourceEmployee(workResourceEmployee);
            toggleModal(setIsModalReturnOpened, true);
        }
    };

    const handleCancelReturnClick = async (workResourceEmployee: IWorkResourceEmployee) => {
        if (workResourceEmployee.returnStatus === ReturnStatusEnum.none) {
            showWarning(MESSAGES.VALIDATION.INVALID_STATUS);
        } else {
            workResourceEmployee.returnStatus = ReturnStatusEnum.none;
            workResourceEmployee.situation = WorkResourceEmployeeStatusEnum.delivered;
            workResourceEmployee.returnDate = undefined;

            setIsLoading(true);

            const response = await workResourceEmployeeService.updateWorkResourceEmployee(workResourceEmployee);

            setIsLoading(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
            await fetchWorkResourcesEmployee();
        }
    }

    const handleSignClick = async (workResourceType: WorkResourceEnum) => {
        setSignItemType(workResourceType);
        toggleModal(setIsModalSignatureOpened, true);
    }

    const handleSignedClick = async (workResourceEmployee: IWorkResourceEmployee) => {
        setSelectedWorkResourceEmployee(workResourceEmployee);
        toggleModal(setIsModalSignedOpened, true);
    }

    const handleDeleteClick = async (workResourceEmployee: IWorkResourceEmployee) => {

        if (!workResourceEmployee.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
        } else {

            setIsLoading(true);

            const response = await workResourceEmployeeService.deleteWorkResourceEmployee(workResourceEmployee.id);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.ACTION.DELETED);
            fetchWorkResourcesEmployee();
        }
    }

    const handleChangeClick = async (workResourceEmployee: IWorkResourceEmployee) => {

        if (!workResourceEmployee.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
        } else {

            setIsLoading(true);

            const response = await workResourceEmployeeService.changeWorkResourceEmployee(workResourceEmployee);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            showSuccess(MESSAGES.SIGN.SUCCESS);
            fetchWorkResourcesEmployee();
        }
    }

    const handleChangeSignClick = async (workResourceEmployee: IWorkResourceEmployee) => {
        setSelectedWorkResourceEmployee(workResourceEmployee);
        toggleModal(setIsModalChangeSignatureOpened, true);
    }

    const onModalClose = () => {
        toggleModal(setIsModalOpened, false);
    };

    const onReturnModalClose = () => {
        toggleModal(setIsModalReturnOpened, false);
        setSelectedWorkResourceEmployee(null);
    };

    const onSignatureModalClose = async () => {
        toggleModal(setIsModalSignatureOpened, false);
        setSelectedWorkResourceEmployee(null);
        await fetchWorkResourcesEmployee();
        setActiveTab(TABS.DELIVERED);
    };

    const onChangeSignatureModalClose = async () => {
        toggleModal(setIsModalChangeSignatureOpened, false);
        setSelectedWorkResourceEmployee(null);
        await fetchWorkResourcesEmployee();
        setActiveTab(TABS.DELIVERED);
    };

    const onSignedModalClose = () => {
        setSelectedWorkResourceEmployee(null);
        toggleModal(setIsModalSignedOpened, false);
    };

    const onAfterSubmit = async () => {
        await fetchWorkResourcesEmployee();
        onModalClose();
    };

    const onReturnAfterSubmit = async () => {
        await fetchWorkResourcesEmployee();
        onReturnModalClose();
    };

    const onSignatureAfterSubmit = async () => {
        await fetchWorkResourcesEmployee();
        onSignatureModalClose();
    };

    const onChangeSignatureAfterSubmit = async () => {
        await fetchWorkResourcesEmployee();
        onChangeSignatureModalClose();
    };

    const hasTools = (workResourceEmployee: IWorkResourceEmployee[]): boolean => {
        return workResourceEmployee.some(p => p.workResource != null && p.workResource.type === WorkResourceEnum.tool);
    };

    const hasPersonalProtectiveEquipments = (workResourceEmployee: IWorkResourceEmployee[]): boolean => {
        return workResourceEmployee.some(p => p.workResource != null && p.workResource.type === WorkResourceEnum.personalProtectiveEquipment);
    };

    const handlePrintClick = async (type: WorkResourceEnum) => {

        if (!selectedEmployee || !selectedEmployee.id) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION)
        } else {

            setIsLoading(true);

            const response = await workResourceEmployeeService.getWorkResourceEmployeeReport(selectedEmployee.id, type);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            const newWindow = window.open("", "_blank");
            if (!newWindow) return;

            newWindow.document.open();
            newWindow.document.write(response.workResourceEmployeeReport?.report || '');
            newWindow.document.close();

            // Aguarda o HTML carregar para chamar print
            newWindow.onload = () => {
                newWindow.focus();
                newWindow.print();
            };
        }
    }

    if (isLoading) {
        return <LoadingPage />
    }

    return (
        <>
            <div className='row h-100'>

                <div className='col-xxl-3 col-xl-3 col-lg-4 col-md-4 col-sm-12'>
                    <Card stretch style={mobileDesign ? { minHeight: 200 } : { minHeight: 400 }}>
                        <CardHeader>
                            <CardLabel icon='Person' iconColor='primary'>
                                <CardTitle tag='div' className='h5'>
                                    {selectedEmployee?.shortName}
                                </CardTitle>
                                {
                                    selectedEmployee?.jobTitle && (
                                        <CardSubTitle tag='div' className='h6'>
                                            {selectedEmployee?.jobTitle?.name}
                                        </CardSubTitle>
                                    )}

                            </CardLabel>
                        </CardHeader>
                        <CardBody isScrollable>
                            <div className='row g-3'>
                                <div className='col-12'>
                                    <Button
                                        icon='Build'
                                        color='primary'
                                        className='w-100 p-3'
                                        isLight={TABS.DELIVERED !== activeTab}
                                        onClick={() => setActiveTab(TABS.DELIVERED)}>
                                        {TABS.DELIVERED}
                                    </Button>
                                </div>
                                <div className='col-12'>
                                    <Button
                                        icon='AssignmentTurnedIn'
                                        color='primary'
                                        className='w-100 p-3'
                                        isLight={TABS.NOT_DELIVERED !== activeTab}
                                        onClick={() => setActiveTab(TABS.NOT_DELIVERED)}>
                                        {TABS.NOT_DELIVERED}
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                        <CardFooter>
                            <CardFooterLeft className='w-100'>
                                <Button
                                    icon='ArrowBack'
                                    color='danger'
                                    isLight
                                    className='w-100 p-3'
                                    tag='a'
                                    to={`../${appMenu.registers.subMenu.employees.path}`}
                                >
                                    Voltar
                                </Button>
                            </CardFooterLeft>
                        </CardFooter>
                    </Card>
                </div>

                <div className='col-xxl-9 col-xl-9 col-lg-8 col-md-8 col-sm-12'>

                    {TABS.DELIVERED === activeTab && (
                        <>
                            <Card stretch style={{ minHeight: 300 }}>
                                <CardHeader borderSize={1}>
                                    <CardLabel icon='Build' iconColor='primary'>
                                        <CardTitle tag='div' className='h5'>
                                            Ferramentas e EPI fornecidos
                                        </CardTitle>
                                    </CardLabel>
                                    <CardActions>
                                        {
                                            workResourcesEmployee &&
                                            workResourcesEmployee.length > 0 &&
                                            hasTools(workResourcesEmployee) && (

                                                <Button
                                                    color='light'
                                                    icon='Print'
                                                    onClick={() => handlePrintClick(WorkResourceEnum.tool)}
                                                >
                                                    Imprimir Ferramentas
                                                </Button>

                                            )
                                        }

                                        {
                                            workResourcesEmployee &&
                                            workResourcesEmployee.length > 0 &&
                                            hasPersonalProtectiveEquipments(workResourcesEmployee) && (

                                                <Button
                                                    color='light'
                                                    icon='Print'
                                                    onClick={() => handlePrintClick(WorkResourceEnum.personalProtectiveEquipment)}
                                                >
                                                    Imprimir EPI
                                                </Button>

                                            )
                                        }
                                    </CardActions>
                                </CardHeader>
                                <CardBody isScrollable className='table-responsive'>
                                    <table className='table table-modern table-hover'>
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Quantidade</th>
                                                <th>Descrição</th>
                                                <th>Data da Entrega</th>
                                                <th>Data Devolução</th>
                                                <th>Situação</th>
                                                <th>Motivo Devolução</th>
                                                <td aria-labelledby='Actions' />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {workResourcesEmployee?.map((item: IWorkResourceEmployee, index) => (
                                                <tr key={item.id}>
                                                    <td style={{ width: 40 }}>{index + 1}</td>
                                                    <td>{item.quantity}</td>
                                                    <td>{item.workResource?.description}</td>
                                                    <td>{dayjs(item.deliveryDate).format('DD/MM/YYYY')}</td>
                                                    <td>{item.returnDate ? dayjs(item.returnDate).format('DD/MM/YYYY') : '-'}</td>
                                                    <td>
                                                        <h5>
                                                            {(() => {
                                                                const { label, color } = getStatusBadge(item.situation);
                                                                return (
                                                                    <Badge color={color} className='align-items-center justify-content-center'>
                                                                        {label}
                                                                    </Badge>
                                                                );
                                                            })()}
                                                        </h5>
                                                    </td>
                                                    <td>{getReturnReason(item.returnStatus)}</td>

                                                    <MoreActions
                                                        id={item.id}
                                                        showEdit={false}
                                                        showDelete={true}
                                                        onDeleteClick={() => handleDeleteClick(item)}
                                                        customActions={[
                                                            ...(item.sign ? [{
                                                                label: 'Assinatura',
                                                                icon: 'AssignmentTurnedIn',
                                                                onClickAction: () => handleSignedClick(item),
                                                            }] : []),

                                                            ...(item.situation === WorkResourceEmployeeStatusEnum.delivered
                                                                ? [
                                                                    {
                                                                        label: 'Trocar',
                                                                        icon: 'ChangeCircle',
                                                                        onClickAction: () => handleChangeClick(item),
                                                                    }
                                                                ]
                                                                : []
                                                            ),

                                                            ...(item.situation === WorkResourceEmployeeStatusEnum.notSigned
                                                                ? [
                                                                    {
                                                                        label: 'Assinar',
                                                                        icon: 'AssignmentTurnedIn',
                                                                        onClickAction: () => handleChangeSignClick(item),
                                                                    }
                                                                ]
                                                                : []
                                                            ),

                                                            ...(item.situation !== WorkResourceEmployeeStatusEnum.notSigned
                                                                ? (
                                                                    item.returnStatus === ReturnStatusEnum.none
                                                                        ? [
                                                                            {
                                                                                label: 'Devolução',
                                                                                icon: 'AssignmentReturn',
                                                                                onClickAction: () => handleReturnClick(item),
                                                                            }
                                                                        ]
                                                                        : [
                                                                            {
                                                                                label: 'Cancelar devolução',
                                                                                icon: 'Cancel',
                                                                                onClickAction: () => handleCancelReturnClick(item),
                                                                            }
                                                                        ]
                                                                )
                                                                : []
                                                            ),

                                                        ]}
                                                    />
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </CardBody>
                            </Card>

                            <ReturnWorkResourceModal
                                isModalOpened={isModalReturnOpened}
                                onModalClose={onReturnModalClose}
                                onAfterSubmit={onReturnAfterSubmit}
                                selectedWorkResourceEmployee={selectedWorkResourceEmployee}
                            />

                            <WorkResourceSignedModal
                                isModalOpened={isModalSignedOpened}
                                onModalClose={onSignedModalClose}
                                selectedWorkResourceEmployee={selectedWorkResourceEmployee}
                                selectedEmployee={selectedEmployee}
                            />
                        </>
                    )}

                    {TABS.NOT_DELIVERED === activeTab && (

                        <Card stretch style={{ minHeight: 300 }}>
                            <CardHeader borderSize={1}>
                                <CardLabel icon='AssignmentTurnedIn' iconColor='primary'>
                                    <CardTitle tag='div' className='h5'>
                                        Ferramentas e EPI não fornecidos
                                    </CardTitle>
                                </CardLabel>
                                <CardActions>

                                    <Button
                                        color='light'
                                        icon='AddCircle'
                                        onClick={handleAddClick}
                                    >
                                        Adicionar
                                    </Button>

                                    {
                                        notDeliveredWorkResourcesEmployee &&
                                        notDeliveredWorkResourcesEmployee.length > 0 &&
                                        hasTools(notDeliveredWorkResourcesEmployee) && (

                                            <Button
                                                color='light'
                                                icon='AssignmentTurnedIn'
                                                onClick={() => handleSignClick(WorkResourceEnum.tool)}
                                            >
                                                Assinar Ferramentas
                                            </Button>

                                        )
                                    }

                                    {
                                        notDeliveredWorkResourcesEmployee &&
                                        notDeliveredWorkResourcesEmployee.length > 0 &&
                                        hasPersonalProtectiveEquipments(notDeliveredWorkResourcesEmployee) && (

                                            <Button
                                                color='light'
                                                icon='AssignmentTurnedIn'
                                                onClick={() => handleSignClick(WorkResourceEnum.personalProtectiveEquipment)}
                                            >
                                                Assinar EPI
                                            </Button>

                                        )
                                    }

                                </CardActions>
                            </CardHeader>
                            <CardBody isScrollable className='table-responsive'>
                                <table className='table table-modern table-hover'>
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>Quantidade</th>
                                            <th>Descrição</th>
                                            <th>Data da Entrega</th>
                                            <th>Situação</th>
                                            <td aria-labelledby='Actions' />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {notDeliveredWorkResourcesEmployee?.map((item: IWorkResourceEmployee, index) => (
                                            <tr key={item.id}>
                                                <td style={{ width: 40 }}>{index + 1}</td>
                                                <td>{item.quantity}</td>
                                                <td>{item.workResource?.description}</td>
                                                <td>{item.deliveryDate ? dayjs(item.deliveryDate).format('DD/MM/YYYY') : '-'}</td>
                                                <td>
                                                    <h5>
                                                        {(() => {
                                                            const { label, color } = getStatusBadge(item.situation);
                                                            return (
                                                                <Badge color={color} className='align-items-center justify-content-center'>
                                                                    {label}
                                                                </Badge>
                                                            );
                                                        })()}
                                                    </h5>
                                                </td>

                                                <td className='text-end'>
                                                    <Tooltips
                                                        delay={100}
                                                        flip={[
                                                            'top',
                                                            'bottom'
                                                        ]}
                                                        placement="right"
                                                        title="Remover"
                                                    >
                                                        <Button
                                                            color='dark'
                                                            isLight
                                                            icon='Delete'
                                                            onClick={() => handleDeleteClick(item)}
                                                            aria-label='Remover'
                                                        />
                                                    </Tooltips>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </CardBody>
                        </Card>
                    )}

                </div>
            </div>

            <WorkResourceEmployeeModal
                isModalOpened={isModalOpened}
                onModalClose={onModalClose}
                onAfterSubmit={onAfterSubmit}
                selectedEmployee={selectedEmployee}
            />

            <WorkResourceSignatureModal
                isModalOpened={isModalSignatureOpened}
                onModalClose={onSignatureModalClose}
                onAfterSubmit={onSignatureAfterSubmit}
                selectedEmployee={selectedEmployee}
                signItemType={signItemType}
            />

            <WorkResourceChangeSignatureModal
                isModalOpened={isModalChangeSignatureOpened}
                onModalClose={onChangeSignatureModalClose}
                onAfterSubmit={onChangeSignatureAfterSubmit}
                selectedEmployee={selectedEmployee}
                selectedWorkResourceEmployee={selectedWorkResourceEmployee}
            />
        </>
    );
}